import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Upload, FileText, Download, Trash2, FolderOpen, Search, Filter, 
  Calendar, File, Image, FileSpreadsheet, ChevronRight, Eye, Edit2, 
  HardDrive, FolderArchive 
} from "lucide-react";
import api from "@/lib/api";

const DOCUMENT_TYPES = [
  { value: "statement", label: "Bank Statement", color: "bg-blue-500/20 text-blue-400" },
  { value: "receipt", label: "Receipt", color: "bg-green-500/20 text-green-400" },
  { value: "invoice", label: "Invoice", color: "bg-purple-500/20 text-purple-400" },
  { value: "other", label: "Other", color: "bg-gray-500/20 text-gray-400" },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function getFileIcon(filename, mimeType) {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (["pdf"].includes(ext)) return <FileText className="w-4 h-4 text-red-400" />;
  if (["csv", "xlsx", "xls"].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-green-400" />;
  if (["png", "jpg", "jpeg"].includes(ext)) return <Image className="w-4 h-4 text-blue-400" />;
  return <File className="w-4 h-4 text-gray-400" />;
}

export default function DocumentsPage({ companyId }) {
  const [documents, setDocuments] = useState([]);
  const [folderStructure, setFolderStructure] = useState([]);
  const [typeBreakdown, setTypeBreakdown] = useState({});
  const [totalSize, setTotalSize] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterType, setFilterType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Upload modal
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadType, setUploadType] = useState("statement");
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear().toString());
  const [uploadMonth, setUploadMonth] = useState((new Date().getMonth() + 1).toString());
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  
  // Edit modal
  const [editDoc, setEditDoc] = useState(null);
  const [editType, setEditType] = useState("");
  const [editNotes, setEditNotes] = useState("");
  
  // Selection
  const [selectedDocs, setSelectedDocs] = useState([]);

  const loadDocuments = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterYear) params.append("year", filterYear);
      if (filterMonth) params.append("month", filterMonth);
      if (filterType) params.append("document_type", filterType);
      if (searchQuery) params.append("search", searchQuery);
      
      const res = await api.get(`/companies/${companyId}/documents?${params.toString()}`);
      setDocuments(res.data.documents || []);
      setFolderStructure(res.data.folder_structure || []);
      setTypeBreakdown(res.data.type_breakdown || {});
      setTotalSize(res.data.total_size_bytes || 0);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [companyId, filterYear, filterMonth, filterType, searchQuery]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error("Please select files to upload");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      uploadFiles.forEach(file => formData.append("files", file));
      formData.append("document_type", uploadType);
      formData.append("year", uploadYear);
      formData.append("month", uploadMonth);
      
      const res = await api.post(`/companies/${companyId}/documents/bulk-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`Uploaded ${res.data.total_uploaded} documents`);
      if (res.data.total_errors > 0) {
        toast.warning(`${res.data.total_errors} files failed to upload`);
      }
      
      setShowUpload(false);
      setUploadFiles([]);
      setUploadNotes("");
      loadDocuments();
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/companies/${companyId}/documents/${doc.document_id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (doc) => {
    if (!confirm(`Delete "${doc.original_filename}"?`)) return;
    
    try {
      await api.delete(`/companies/${companyId}/documents/${doc.document_id}`);
      toast.success("Document deleted");
      loadDocuments();
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const handleEditSave = async () => {
    if (!editDoc) return;
    
    try {
      const params = new URLSearchParams();
      if (editType) params.append("document_type", editType);
      if (editNotes !== undefined) params.append("notes", editNotes);
      
      await api.put(`/companies/${companyId}/documents/${editDoc.document_id}?${params.toString()}`);
      toast.success("Document updated");
      setEditDoc(null);
      loadDocuments();
    } catch (err) {
      toast.error("Failed to update document");
    }
  };

  const handleFolderClick = (year, month) => {
    setFilterYear(year.toString());
    setFilterMonth(month.toString());
  };

  const clearFilters = () => {
    setFilterYear("");
    setFilterMonth("");
    setFilterType("");
    setSearchQuery("");
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const getTypeBadge = (type) => {
    const typeConfig = DOCUMENT_TYPES.find(t => t.value === type) || DOCUMENT_TYPES[3];
    return <Badge className={typeConfig.color}>{typeConfig.label}</Badge>;
  };

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Select a company to view documents
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#d4c4b0]">Document Storage</h1>
          <p className="text-[#8b9299] text-sm mt-1">Organize and manage your financial documents for audit</p>
        </div>
        <Button onClick={() => setShowUpload(true)} className="btn-primary-gradient" data-testid="upload-documents-btn">
          <Upload className="w-4 h-4 mr-2" /> Upload Documents
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FolderArchive className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Total Documents</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{documents.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Storage Used</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{formatFileSize(totalSize)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Statements</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{typeBreakdown.statement || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Folders</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{folderStructure.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Folder Navigation */}
        <Card className="bg-card border-border lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#d4c4b0] flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-[#e85d24]" /> Folders
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              <button
                onClick={clearFilters}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${
                  !filterYear && !filterMonth ? "bg-[#e85d24]/10 text-[#e85d24]" : "text-[#8b9299] hover:bg-secondary"
                }`}
                data-testid="folder-all"
              >
                <span className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" /> All Documents
                </span>
                <span className="text-xs">{documents.length}</span>
              </button>
              
              {folderStructure.map((folder, idx) => (
                <button
                  key={idx}
                  onClick={() => handleFolderClick(folder.year, folder.month)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between transition-colors ${
                    filterYear === folder.year.toString() && filterMonth === folder.month.toString()
                      ? "bg-[#e85d24]/10 text-[#e85d24]"
                      : "text-[#8b9299] hover:bg-secondary"
                  }`}
                  data-testid={`folder-${folder.year}-${folder.month}`}
                >
                  <span className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3" />
                    {MONTHS[folder.month - 1]} {folder.year}
                  </span>
                  <Badge variant="outline" className="text-xs">{folder.count}</Badge>
                </button>
              ))}
              
              {folderStructure.length === 0 && (
                <p className="text-xs text-[#6b7280] text-center py-4">No folders yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Documents List */}
        <Card className="bg-card border-border lg:col-span-3">
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <CardTitle className="text-sm font-medium text-[#d4c4b0]">
                {filterYear && filterMonth 
                  ? `${MONTHS[parseInt(filterMonth) - 1]} ${filterYear}` 
                  : "All Documents"}
              </CardTitle>
              
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#6b7280]" />
                  <Input
                    placeholder="Search files..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-48 bg-secondary border-border text-[#d4c4b0] h-9"
                    data-testid="search-documents"
                  />
                </div>
                
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-32 bg-secondary border-border text-[#d4c4b0] h-9">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all" className="text-[#d4c4b0]">All Types</SelectItem>
                    {DOCUMENT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-[#d4c4b0]">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {(filterYear || filterMonth || filterType || searchQuery) && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#8b9299]">
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-6 h-6 border-2 border-[#e85d24] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : documents.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-[#6b7280]">
                <FolderOpen className="w-12 h-12 mb-3 opacity-50" />
                <p>No documents found</p>
                <Button variant="link" onClick={() => setShowUpload(true)} className="text-[#e85d24] mt-2">
                  Upload your first document
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-[#8b9299] w-8"></TableHead>
                      <TableHead className="text-[#8b9299]">File Name</TableHead>
                      <TableHead className="text-[#8b9299]">Type</TableHead>
                      <TableHead className="text-[#8b9299]">Size</TableHead>
                      <TableHead className="text-[#8b9299]">Upload Date</TableHead>
                      <TableHead className="text-[#8b9299] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.document_id} className="border-border hover:bg-secondary/50" data-testid={`doc-row-${doc.document_id}`}>
                        <TableCell className="p-2">
                          {getFileIcon(doc.original_filename, doc.mime_type)}
                        </TableCell>
                        <TableCell className="text-[#d4c4b0] font-medium">
                          <div>
                            <p className="truncate max-w-[200px]" title={doc.original_filename}>
                              {doc.original_filename}
                            </p>
                            {doc.notes && (
                              <p className="text-xs text-[#6b7280] truncate max-w-[200px]">{doc.notes}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getTypeBadge(doc.document_type)}</TableCell>
                        <TableCell className="text-[#8b9299]">{formatFileSize(doc.file_size)}</TableCell>
                        <TableCell className="text-[#8b9299] text-sm">
                          {new Date(doc.upload_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-[#8b9299] hover:text-[#d4c4b0]"
                              onClick={() => handleDownload(doc)}
                              data-testid={`download-${doc.document_id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-[#8b9299] hover:text-[#d4c4b0]"
                              onClick={() => {
                                setEditDoc(doc);
                                setEditType(doc.document_type);
                                setEditNotes(doc.notes || "");
                              }}
                              data-testid={`edit-${doc.document_id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleDelete(doc)}
                              data-testid={`delete-${doc.document_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Upload Documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* File Input */}
            <div>
              <Label className="text-[#8b9299] text-sm">Select Files</Label>
              <div className="mt-1.5 border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-[#e85d24]/50 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.csv,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg,.txt"
                  data-testid="file-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-[#6b7280] mb-2" />
                  <p className="text-sm text-[#8b9299]">
                    {uploadFiles.length > 0 
                      ? `${uploadFiles.length} file(s) selected`
                      : "Click to select files or drag and drop"}
                  </p>
                  <p className="text-xs text-[#6b7280] mt-1">PDF, CSV, Excel, Word, Images, TXT</p>
                </label>
              </div>
              {uploadFiles.length > 0 && (
                <div className="mt-2 space-y-1 max-h-24 overflow-y-auto">
                  {uploadFiles.map((file, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-[#8b9299]">
                      <File className="w-3 h-3" />
                      <span className="truncate">{file.name}</span>
                      <span>({formatFileSize(file.size)})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Document Type */}
            <div>
              <Label className="text-[#8b9299] text-sm">Document Type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="upload-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {DOCUMENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#8b9299] text-sm">Year</Label>
                <Select value={uploadYear} onValueChange={setUploadYear}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="upload-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()} className="text-white">{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Month</Label>
                <Select value={uploadMonth} onValueChange={setUploadMonth}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="upload-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={(i + 1).toString()} className="text-white">{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)} className="border-border text-white">
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploadFiles.length === 0 || uploading}
              className="btn-primary-gradient"
              data-testid="upload-submit-btn"
            >
              {uploading ? "Uploading..." : `Upload ${uploadFiles.length} File(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDoc} onOpenChange={(open) => !open && setEditDoc(null)}>
        <DialogContent className="bg-card border-border" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Edit Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[#8b9299] text-sm">File Name</Label>
              <p className="text-white mt-1">{editDoc?.original_filename}</p>
            </div>
            
            <div>
              <Label className="text-[#8b9299] text-sm">Document Type</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {DOCUMENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-[#8b9299] text-sm">Notes</Label>
              <Textarea 
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Add notes about this document..."
                className="mt-1.5 bg-secondary border-border text-white"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDoc(null)} className="border-border text-white">
              Cancel
            </Button>
            <Button onClick={handleEditSave} className="btn-primary-gradient" data-testid="edit-save-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
